import type { CountryName } from '@skytech/types'
import { mapKeysToCamelCase } from '@skytech/utils'

import {
  API_ROUTE_ADMIN_COST_OBJECT_APP_AWARENESS_MESSAGE,
  API_ROUTE_ADMIN_COST_OBJECT_AWARENESS_EMAIL,
  API_ROUTE_ADMIN_COST_OBJECT_POLICY_SUBSCRIPTION,
  API_ROUTE_ADMIN_COST_OBJECT_POLICY_SUBSCRIPTION_REMOVE,
  API_ROUTE_ADMIN_COST_OBJECT_POLICY_SUBSCRIPTION_UNLOCK_ORDERING,
  API_ROUTE_COST_OBJECTS_ASSIGN_ECOM_POLICIES,
  API_ROUTE_ADMIN_EMPLOYEE_CONSENTS,
  API_ROUTE_ADMIN_USERS,
  API_ROUTE_ADMIN_TEXT_TEMPLATE_CODES,
  API_ROUTE_ADMIN_WHITE_LABELS,
  API_ROUTE_ADMIN_PHONE_SEND_SMS,
  API_ROUTE_ADMIN_PHONE_GROUPS,
  API_ROUTE_ADMIN_PHONE_GROUP_NUMBERS,
  API_ROUTE_ECOM_POLICY_ORDER_ADD_DOWN_PAYMENT,
  API_ROUTE_ADMIN_EMPLOYEE_CONSENTS_REVOKE,
  API_ROUTE_ADMIN_PHONE_BOOKS,
  API_ROUTE_ADMIN_PHONE_BOOKS_UPDATE_NUMBER,
  API_ROUTE_ADMIN_PHONE_GROUP_NUMBER,
  API_ROUTE_ECOM_POLICY_DELETE_ORDER_DOWN_PAYMENT,
  API_ROUTE_COST_OBJECTS_ASSIGN_TELE_POLICIES,
  API_ROUTE_ADMIN_USER_GENERATE_PASSWORD,
  API_ROUTE_ADMIN_USES_ALIASES,
  API_ROUTE_ADMIN_AUTH,
  API_ROUTE_ADMIN_CUSTOMER_ADDRESSES,
  API_ROUTE_ECOM_POLICY_CUSTOMERS,
  API_ROUTE_ADMIN_CUSTOMER_ADDRESSES_BY_ID,
  API_ROUTE_ADMIN_EMPLOYEE_CONSENT_PUBLISH,
  API_ROUTE_ADMIN_EMPLOYEE_CONSENT_DETAILS,
} from '@/resources/api-routes'
import { get, post, update, del } from '@/util/auth'

import type { NumberRecordType } from '../sms-sender/types'

import {
  addAsset,
  updateAsset,
  getAsset,
  getAssetList,
  getLeasedAssetList,
  getAssetTypes,
  deleteAsset,
  activateAsset,
} from './assets'
import {
  getCostObjectDetails,
  getCostObjectList,
  postCostObject,
  switchToCompanyNumber,
} from './cost-objects'
import { updateDepartment, getDepartmentsList, createDepartment } from './departments'
import {
  getDrillDownCostObjects,
  getDrillDownDepartments,
  getDrillDownCostObjectsByDepartments,
  getDrillDownProductsByDepartmentsAndCostObject,
  getDrillDownCostObjectsReport,
  getDrillDownProductByCostObjectsReport,
} from './drill-down'
import {
  getDrillDownGroups,
  getDrillDownCategories,
  getDrillDownCustomerTotals,
  getDrillDownProducts,
} from './drill-down'
import {
  activateFeature,
  deactivateFeature,
  getFeatureActivities,
  getFeatureDetails,
  getFeatureList,
  getFeatureSalesPitch,
  getFeatureTermOfService,
  getCustomersWithActiveFeature,
} from './features'
import { getEmployees } from './getEmployees'
import { getInvoiceDetails, getInvoicesList } from './invoices'
import {
  postSetupExportSettings,
  getAccountingReports,
  getSalaryDeductionDeviceReports,
  getSalaryDeductionUsageReports,
  getOffboardingReports,
  getTaxAdvantageReports,
  getConsumptionReports,
  getSetupExportSettings,
  getNewNumberOrdersReports,
  getCustomerSpecificReports,
  getReportCustomers,
} from './reports'
import { getConsentDetails } from './reports/consents'
import { getConsentReports, getConsents } from './reports/consents'
import { getConsentHistoryPerCostObject } from './reports/consents/getConsentHistoryPerCostObject'
import { getBinaryExports } from './reports/getExports'
import { getSMSLogsList, getSMSLogReceiversList, getActiveNumbersList } from './sms-sender'
import {
  createTelePolicy,
  updateTelePolicy,
  deleteTelePolicy,
  getTelePolicyTemplatesList,
  getTelePolicyList,
} from './tele-policies'
import {
  getTextTemplateList,
  getTextTemplateDefaultValues,
  getTextTemplateDetails,
  postTextTemplate,
  updateTextTemplate,
} from './text-templates'
import {
  getTradeInOrderStatus,
  getTradeInOrders,
  getCustomerTradeInAgreements,
  getTradeInCustomerAgreements,
  getTradeInAssetOrder,
  sendTradeInPriceRequest,
  updateTradeInOrders,
} from './trade-in'
import type { UserTypeOriginal } from './types'

const IS_NEW_BACKEND_API = true

const adminResources = {
  getAddressList: async () => {
    type OriginalAddress = {
      id: number
      address: string
      zip: string
      city: string
      countryName: CountryName
      countryId: number
      isGlobal: boolean
      customerName: string
      customerId: number | null
    }
    const addresses = await get<OriginalAddress[]>(
      API_ROUTE_ADMIN_CUSTOMER_ADDRESSES,
      IS_NEW_BACKEND_API
    )

    return addresses
  },
  getTelePolicyList,
  getTelePolicyTemplatesList,
  getTextTemplateList,
  getTextTemplateDefaultValues,
  getTextTemplateDetails,
  getWhiteLabelList: () =>
    get<
      {
        id: number
        name: string
      }[]
    >(API_ROUTE_ADMIN_WHITE_LABELS, IS_NEW_BACKEND_API),
  getTextTemplateCodes: () =>
    get<string[]>(API_ROUTE_ADMIN_TEXT_TEMPLATE_CODES, IS_NEW_BACKEND_API),
  getCostObjectList,
  getAsset,
  getAssetList,
  getLeasedAssetList,
  getEmployees,
  getCostObjectDetails,
  getCustomers: async () => {
    type OriginalCustomer = {
      id: number
      name: string
      org_no: string
      customer_head_id: number
      customer_head_name: string
      customer_head_org_no: string
    }
    const customers = await get<OriginalCustomer[]>(API_ROUTE_ECOM_POLICY_CUSTOMERS)
    const parsedCustomers = mapKeysToCamelCase(customers)

    return parsedCustomers
  },
  getDrillDownCustomerTotals,
  getDrillDownCategories,
  getDrillDownGroups,
  getDrillDownProducts,
  getDrillDownCostObjects,
  getDrillDownDepartments,
  getDrillDownCostObjectsByDepartments,
  getDrillDownProductsByDepartmentsAndCostObject,
  getDrillDownCostObjectsReport,
  getDrillDownProductByCostObjectsReport,
  getAssetTypes,
  getConsents,
  getConsentReports,
  getConsentDetails,
  getConsentHistoryPerCostObject,
  getDepartmentsList,
  getSMSLogsList,
  getSMSLogReceiversList,
  getActiveNumbersList,
  getUserAliasesList: async () =>
    await get<
      {
        label: string
        value: string
      }[]
    >(API_ROUTE_ADMIN_USES_ALIASES, IS_NEW_BACKEND_API),
  getPhoneGroupsList: async () =>
    await get<
      {
        id: number
        name: string
        userId: number
        customerId: number
      }[]
    >(API_ROUTE_ADMIN_PHONE_GROUPS, IS_NEW_BACKEND_API),
  getInvoicesList,
  getInvoiceDetails,
  getUsersList: async () => {
    const costObjects = await get<{
      users: (Omit<UserTypeOriginal, 'phoneNumber'> & { phoneNumber: number | string })[]
    }>(API_ROUTE_ADMIN_USERS, IS_NEW_BACKEND_API)

    return costObjects.users.map(user => ({
      ...user,
      phoneNumber: user.phoneNumber ? String(user.phoneNumber) : null,
    }))
  },
  getUserDetails: (id: number) =>
    get<UserTypeOriginal>(`${API_ROUTE_ADMIN_USERS}/${id}`, IS_NEW_BACKEND_API),
  getUserCustomersList: () =>
    get<
      {
        id: number
        customerNo: string
        orgNo: string
        vatNo: string | null
        name: string
        refactoring: boolean
        address1: string
        address2: string | null
        zip: string
        city: string
        billingAddress1: string
        billingAddress2: string | null
        billingZip: string
        billingCity: string
        customerStatus: number
        useAltPdfFront: number
        receiveMail: number
        mailTemplate: string
        mailColorScheme: string | null
        customerHeadId: number
        adminCostPerCostObject: boolean
        mailTemplateDepartment: string | null
        comment: string
        invoiceQuantity: number
        invoiceCost: string
        initArpu: string
        initUarpu: string
        initBarpu: string
        countryId: number
        locale: string
        whitelabelId: number
        departmentMailSendDate: number
        tciIndexPercentage: string
        salaryDeductAppMailTemplate: string | null
        customerCustomUrlParam: string | null
        mailTemplateAdmin: string | null
        mailTemplateUserReminder: string | null
        dealerUserId: number
        deviceDealerUserId: number | null
        devicePolicyMessage: string
        created: string
        lastUpdate: string
        billingCycleMonths: number
        devicePolicyVendorId: number
        numberOfEmployees: number | null
        internalCompanyIdentifier: string | null
        customerGuid: string
      }[]
    >(`${API_ROUTE_ADMIN_USERS}/customers`, IS_NEW_BACKEND_API),
  getAccountingReports,
  getSalaryDeductionDeviceReports,
  getSalaryDeductionUsageReports,
  getConsumptionReports,
  getOffboardingReports,
  getTaxAdvantageReports,
  getSetupExportSettings,
  getNewNumberOrdersReports,
  getBinaryExports,
  getTradeInOrders,
  getTradeInOrderStatus,
  getCustomerTradeInAgreements,
  getTradeInAssetOrder,
  getTradeInCustomerAgreements,
  updateTradeInOrders,

  // Mutations
  postAddress: async (payload: {
    countryId: number | null
    customerId: number | null
    address: string
    zip: string
    city: string
  }) => {
    const address = await post<Address>(
      API_ROUTE_ADMIN_CUSTOMER_ADDRESSES,
      payload,
      IS_NEW_BACKEND_API
    )
    return address
  },

  updateAddress: async (payload: {
    id: number
    data: {
      countryId: number | null
      customerId: number | null
      address: string
      zip: string
      city: string
    }
  }) => {
    const { id, data } = payload
    const address = await update<Address>(
      API_ROUTE_ADMIN_CUSTOMER_ADDRESSES_BY_ID.replace(':id', String(id)),
      data,
      IS_NEW_BACKEND_API
    )
    return address
  },
  deleteAddress: async (addressId: number) =>
    await del(
      API_ROUTE_ADMIN_CUSTOMER_ADDRESSES_BY_ID.replace(':id', String(addressId)),
      IS_NEW_BACKEND_API
    ),
  postTextTemplate,
  updateTextTemplate,
  createUser: (payload: Omit<UserTypeOriginal, 'id' | 'username'>) =>
    post<UserTypeOriginal>(API_ROUTE_ADMIN_USERS, payload, IS_NEW_BACKEND_API),
  updateUser: (payload: Partial<UserTypeOriginal>, id: number) =>
    update<UserTypeOriginal>(`${API_ROUTE_ADMIN_USERS}/${id}`, payload, IS_NEW_BACKEND_API),
  deleteUser: (id: number) => del(`${API_ROUTE_ADMIN_USERS}/${id}`, IS_NEW_BACKEND_API),
  changePassword: (payload: {
    currentPassword: string
    newPassword: string
    confirmPassword: string
  }) => post<null>(`${API_ROUTE_ADMIN_AUTH}/change-password`, payload, IS_NEW_BACKEND_API),
  generateUserPassword: (userId: number) =>
    post(
      API_ROUTE_ADMIN_USER_GENERATE_PASSWORD.replace(':userId', String(userId)),
      {},
      IS_NEW_BACKEND_API
    ),
  postCostObject,
  switchToCompanyNumber,
  deleteAsset,
  activateAsset,
  updateDepartment,
  createDepartment,
  sendSMS: async ({
    payload,
  }: {
    payload: {
      message: string
      receivers: Omit<NumberRecordType, 'id'>[]
      sender: string
      isPrivate: boolean
    }
  }) => post(API_ROUTE_ADMIN_PHONE_SEND_SMS, payload, IS_NEW_BACKEND_API),
  createPhoneGroup: async (payload: { name: string }) =>
    post<{
      id: number
      name: string
      userId: number
      customerId: number
    }>(API_ROUTE_ADMIN_PHONE_GROUPS, payload, IS_NEW_BACKEND_API),
  addNumbersToPhoneGroup: async (payload: {
    groupId: number
    numbers: Omit<NumberRecordType, 'id'>[]
  }) =>
    post<{
      id: number
      name: string
      userId: number
      customerId: number
    }>(
      API_ROUTE_ADMIN_PHONE_GROUP_NUMBERS.replace(':groupId', payload.groupId.toString()),
      payload,
      IS_NEW_BACKEND_API
    ),
  updatePhoneGroupNumber: async (payload: {
    groupId: number
    numberId: number
    data: Omit<NumberRecordType, 'id'>
  }) => {
    const { data, groupId, numberId } = payload
    await post<{
      id: number
      name: string
      userId: number
      customerId: number
    }>(
      API_ROUTE_ADMIN_PHONE_GROUP_NUMBER.replace(':groupId', String(groupId)).replace(
        ':numberId',
        String(numberId)
      ),
      data,
      IS_NEW_BACKEND_API
    )
  },
  addNumbersToPhoneBook: async (payload: { numbers: Omit<NumberRecordType, 'id'>[] }) =>
    post<{
      id: number
      name: string
      userId: number
      customerId: number
    }>(API_ROUTE_ADMIN_PHONE_BOOKS, payload, IS_NEW_BACKEND_API),
  updatePhoneBookNumber: async (payload: {
    numberId: number
    data: Omit<NumberRecordType, 'id'>
  }) =>
    post<{
      id: number
      name: string
      userId: number
      customerId: number
    }>(
      API_ROUTE_ADMIN_PHONE_BOOKS_UPDATE_NUMBER.replace(':id', payload.numberId.toString()),
      payload.data,
      IS_NEW_BACKEND_API
    ),
  createTelePolicy,
  updateTelePolicy,
  deleteTelePolicy,
  postCostObjectPolicySubscription: ({
    costObjectId,
    policyId,
  }: {
    costObjectId: number
    policyId: number
  }) =>
    post<null>(
      API_ROUTE_ADMIN_COST_OBJECT_POLICY_SUBSCRIPTION.replace(
        ':costObjectId',
        String(costObjectId)
      ).replace(':policyId', String(policyId))
    ),
  sendCostObjectAppAwarenessMessage: (costObjectId: number) =>
    post<null>(
      API_ROUTE_ADMIN_COST_OBJECT_APP_AWARENESS_MESSAGE.replace(
        ':costObjectId',
        String(costObjectId)
      )
    ),
  sendCostObjectAwarenessEmail: (costObjectId: number) =>
    post<null>(
      API_ROUTE_ADMIN_COST_OBJECT_AWARENESS_EMAIL.replace(':costObjectId', String(costObjectId))
    ),
  deleteCostObjectPolicySubscription: ({
    costObjectId,
    policyId,
  }: {
    costObjectId: number
    policyId: number
  }) =>
    post<null>(
      API_ROUTE_ADMIN_COST_OBJECT_POLICY_SUBSCRIPTION_REMOVE.replace(
        ':costObjectId',
        String(costObjectId)
      ).replace(':policyId', String(policyId))
    ),
  unlockOrderingOnCostObjectPolicySubscription: ({
    costObjectId,
    policyId,
  }: {
    costObjectId: number
    policyId: number
  }) =>
    post<null>(
      API_ROUTE_ADMIN_COST_OBJECT_POLICY_SUBSCRIPTION_UNLOCK_ORDERING.replace(
        ':costObjectId',
        String(costObjectId)
      ).replace(':policyId', String(policyId))
    ),
  assignEcomPolicies: (payload: {
    costObjectIds: number[]
    addPolicyList: number[]
    removePolicyList: number[]
  }) => post<null>(API_ROUTE_COST_OBJECTS_ASSIGN_ECOM_POLICIES, payload),
  assignTelePolicies: (payload: { costObjectIds: number[]; telePolicyId: number | null }) =>
    post<null>(API_ROUTE_COST_OBJECTS_ASSIGN_TELE_POLICIES, payload, IS_NEW_BACKEND_API),
  addAsset,
  updateAsset,
  postEmployeeConsent: async (payload: { text: string; title: string; customerId?: number }) =>
    post<null>(API_ROUTE_ADMIN_EMPLOYEE_CONSENTS, payload, IS_NEW_BACKEND_API),
  publishEmployeeConsent: async ({ id }: { id: string }) =>
    post<null>(
      API_ROUTE_ADMIN_EMPLOYEE_CONSENT_PUBLISH.replace(':consentId', id),
      {},
      IS_NEW_BACKEND_API
    ),
  updateEmployeeConsent: async (payload: { text: string; title: string; id: number }) =>
    post<null>(
      API_ROUTE_ADMIN_EMPLOYEE_CONSENT_DETAILS.replace(':consentId', String(payload.id)),
      { text: payload.text, title: payload.title },
      IS_NEW_BACKEND_API
    ),

  deleteDownPayment: ({ downPaymentId }: { downPaymentId: number }) =>
    post<null>(
      API_ROUTE_ECOM_POLICY_DELETE_ORDER_DOWN_PAYMENT.replace(
        ':downPaymentId',
        String(downPaymentId)
      )
    ),
  addDownPayment: ({ amount, downPaymentId }: { amount: number; downPaymentId: number }) =>
    post<null>(
      API_ROUTE_ECOM_POLICY_ORDER_ADD_DOWN_PAYMENT.replace(':downPaymentId', String(downPaymentId)),
      {
        amount,
      }
    ),
  revokeConsent: ({ consentId, costObjectId }: { consentId: number; costObjectId: number }) =>
    post<null>(
      API_ROUTE_ADMIN_EMPLOYEE_CONSENTS_REVOKE.replace(':consentId', String(consentId)).replace(
        ':costObjectId',
        String(costObjectId)
      ),
      {},
      IS_NEW_BACKEND_API
    ),
  postSetupExportSettings,
  getFeatureList,
  getCustomersWithActiveFeature,
  getFeatureActivities,
  getFeatureDetails,
  getFeatureSalesPitch,
  getFeatureTermOfService,
  activateFeature,
  deactivateFeature,
  sendTradeInPriceRequest,
  getCustomerSpecificReports,
  getReportCustomers,
}

export default adminResources

export type Address = Awaited<ReturnType<typeof adminResources.getAddressList>>[number]
export type AddAddressPayload = Parameters<typeof adminResources.postAddress>[number]
export type UpdateAddressPayload = Parameters<typeof adminResources.updateAddress>[number]

export type AssignEcomPoliciesPayload = Parameters<typeof adminResources.assignEcomPolicies>[number]

export type Customer = Awaited<ReturnType<typeof adminResources.getCustomers>>[number]

export type EmployeeConsent = Awaited<ReturnType<typeof getConsents>>[number]
export type EmployeeConsentReport = Awaited<ReturnType<typeof getConsentReports>>
export type EmployeeConsentEmployee = Awaited<ReturnType<typeof getConsentReports>>[number]
export type EmployeeConsentDetails = Awaited<ReturnType<typeof getConsentDetails>>
export type EmployeeConsentHistory = Awaited<
  ReturnType<typeof getConsentHistoryPerCostObject>
>[number]

export type CostObject = Awaited<ReturnType<typeof getCostObjectList>>[number]
export type CostObjectDetails = Awaited<ReturnType<typeof getCostObjectDetails>>
export type EcomPolicySubscription = CostObjectDetails['ecomPolicySubscriptions'][number]

export type DrillDownCustomer = Awaited<ReturnType<typeof getDrillDownCustomerTotals>>[number]
export type DrillDownEntity = Awaited<ReturnType<typeof getDrillDownProducts>>['entity']
export type DrillDownProductCategory = Awaited<
  ReturnType<typeof getDrillDownCategories>
>['rows'][number]
export type DrillDownProduct = Awaited<ReturnType<typeof getDrillDownProducts>>['rows'][number]
export type DrillDownProductGroup = Awaited<ReturnType<typeof getDrillDownGroups>>['rows'][number]

export type Asset = Awaited<ReturnType<typeof getAssetList>>[number]
export type LeasedAsset = Awaited<ReturnType<typeof getLeasedAssetList>>['assets'][number]
export type ActiveEndingOptions = Awaited<
  ReturnType<typeof getLeasedAssetList>
>['activeEndingOptions'][number]
export type AssetDownPayment = Awaited<ReturnType<typeof getAsset>>['downPayments'][number]
export type AssetExtended = Awaited<ReturnType<typeof getAsset>>
export type UpdateAssetPayload = Parameters<typeof updateAsset>[number]['payload']

export type Department = Awaited<ReturnType<typeof getDepartmentsList>>[number]

export type ReportFilters = Parameters<typeof getAccountingReports>[number]
export type ReportType = Parameters<typeof getAccountingReports>[number]['selectedReportType']
export type ReportSetupExportSettings = Awaited<ReturnType<typeof getSetupExportSettings>>
export type AccountingReport = Awaited<ReturnType<typeof getAccountingReports>>[number]
export type SalaryDeductionDeviceReport = Awaited<
  ReturnType<typeof getSalaryDeductionDeviceReports>
>[number]
export type SalaryDeductionUsageReport = Awaited<
  ReturnType<typeof getSalaryDeductionUsageReports>
>[number]
export type ConsumptionReport = Awaited<ReturnType<typeof getConsumptionReports>>[number]
export type OffboardingReport = Awaited<ReturnType<typeof getOffboardingReports>>[number]
export type TaxAdvantageReport = Awaited<ReturnType<typeof getTaxAdvantageReports>>[number]
export type NewNumberOrdersReport = Awaited<ReturnType<typeof getNewNumberOrdersReports>>[number]

export type Invoice = Awaited<ReturnType<typeof getInvoicesList>>[number]
export type InvoiceRow = Awaited<ReturnType<typeof getInvoicesList>>[number]['rows'][number]
export type Vendor = Awaited<ReturnType<typeof getInvoicesList>>[number]['vendor']

export type LogSmsPush = Awaited<ReturnType<typeof getSMSLogsList>>[number]
export type ActiveNumbers = Awaited<ReturnType<typeof getActiveNumbersList>>[number]
export type SendSMS = Parameters<typeof adminResources.sendSMS>[number]['payload']

export type User = Awaited<ReturnType<typeof adminResources.getUsersList>>[number]

export type TextTemplate = Awaited<ReturnType<typeof getTextTemplateList>>[number]

export type SalaryDeductionProfile = Awaited<
  ReturnType<typeof adminResources.getTelePolicyList>
>[number]

export type DrillDownParams = Parameters<typeof adminResources.getDrillDownProducts>[number]
export type DrillDownType = NonNullable<DrillDownParams['type']>
export type CostObjectReport = Awaited<
  ReturnType<typeof adminResources.getDrillDownCostObjectsByDepartments>
>['rows'][number]
export type ProductByCostObject = Awaited<
  ReturnType<typeof getDrillDownProductByCostObjectsReport>
>['rows'][number]
export type DepartmentReport = Awaited<ReturnType<typeof getDrillDownDepartments>>['rows'][number]
export type ProductReport = Awaited<
  ReturnType<typeof getDrillDownProductsByDepartmentsAndCostObject>
>['rows'][number]
export type ProductGroupCategory = Awaited<ReturnType<typeof getDrillDownProducts>>['rows'][number]
export type DrillDownCostObject = Awaited<
  ReturnType<typeof getDrillDownCostObjects>
>['rows'][number]
export type AssetUserType = Awaited<ReturnType<typeof getAssetTypes>>['userTypes'][number]['type']
export type AssetStatus = Awaited<ReturnType<typeof getAssetTypes>>['statuses'][number]['status']
export type TradeInOrder = Awaited<ReturnType<typeof getTradeInOrders>>['orders'][number]
export type TradeInOrderCounter = Awaited<ReturnType<typeof getTradeInOrders>>['orderCounter']
export type Employee = Awaited<ReturnType<typeof getEmployees>>[number]
export type Feature = Awaited<ReturnType<typeof adminResources.getFeatureList>>[number]
export type FeatureDetails = Awaited<ReturnType<typeof adminResources.getFeatureDetails>>
